// src/subscreens/mechanicadmin/earnings/MechanicEarnings.jsx

import React, { useState, useEffect, useContext } from "react";
import {
  FiTrendingUp,
  FiCheckCircle,
  FiClock,
  FiPlus,
  FiX,
  FiRefreshCw,
  FiLock,
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import MechanicAdminLayout from "../../../layout/mechanic/MechanicMasterLayout.js";
import "../../../assets/css/mechanic/earning/Earning.css";
import AppContext from "../../../context/AppContext.js";
import axios from "axios";
import Swal from "sweetalert2";

/* ── Quick Topup Amounts ── */
const quickAmounts = [1000, 2000, 5000, 10000, 20000, 50000];

/* ── Fallback minimum (backend "minimumTopup" overrides this once loaded) ── */
const DEFAULT_MINIMUM_TOPUP = 1000;

export default function MechanicEarnings() {
  const { baseUrl, baseUrlForFile, loggedInMechanic, authToken } = useContext(AppContext);

  const [activeCard, setActiveCard] = useState(null);

  /* ── Topup Modal / UI State ── */
  const [showTopup, setShowTopup] = useState(false);

  // ── Wallet balance ab persisted DB column sy aata hai (viewTopupRequests) ──
  const [walletBalance, setWalletBalance] = useState(0);
  const [minimumTopup, setMinimumTopup] = useState(DEFAULT_MINIMUM_TOPUP);

  const [selectedAmt, setSelectedAmt] = useState(0);
  const [customAmt, setCustomAmt] = useState("");
  const [loading, setLoading] = useState(false);

  /* ── Topup Requests List (View) ── */
  const [topupRequests, setTopupRequests] = useState([]);

  // ── NOTE: loggedInMechanic.id sirf localStorage/UI purposes k liye rakha
  //    hai (jese pendingTopup record mein). Sab BACKEND calls ab sirf
  //    Authorization token py depend karte hain — koi bhi API request
  //    body mein srmechanicadmin_id nahi bhejti, taake koi mechanic
  //    apna token use kar ke kisi aur mechanic ka data access na kar sakay. ──
  const mechanic =
    typeof loggedInMechanic === "string"
      ? JSON.parse(loggedInMechanic)
      : loggedInMechanic;

  const srmechanicadmin_id = mechanic?.id;

  /* ── Topup Handlers ── */
  const openTopup = () => {
    setSelectedAmt(0);
    setCustomAmt("");
    setShowTopup(true);
  };

  const closeTopup = () => {
    setShowTopup(false);
  };

  const handleQuickAmt = (amt) => {
    setSelectedAmt(amt);
    setCustomAmt("");
  };

  const handleCustomAmt = (val) => {
    setCustomAmt(val);
    setSelectedAmt(0);
  };

  const finalAmt = selectedAmt || parseInt(customAmt) || 0;

  // ── Pay with Stripe — same pattern as EquipmentBookingModal.jsx:
  //    save the pending topup to localStorage, get a Stripe checkout
  //    session from the backend, then redirect the browser to Stripe.
  //    The actual Srtopup record + wallet credit happens on the
  //    /topup-success page (handleTopupSuccess backend endpoint,
  //    which identifies the mechanic from the Authorization token —
  //    NOT from this localStorage value). ──
  const handlePayWithStripe = () => {
    if (finalAmt < minimumTopup) return;

    setLoading(true);

    // localStorage record sirf UI/receipt reference k liye — actual
    // wallet credit backend py token-authenticated mechanic ko milta hai.
    const pendingTopup = {
      amount: finalAmt,
      paymentmethod: "stripe",
      status: "pending",
    };
    localStorage.setItem("pendingTopup", JSON.stringify(pendingTopup));

    axios
      .post(
        `${baseUrl}/mechanicadmin/topup/createTopupCheckoutSession`,
        { amount: finalAmt },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => {
        setLoading(false);
        const stripeUrl = res.data.url;

        if (res.data.success && stripeUrl) {
          window.location.href = stripeUrl;
          return;
        }

        Swal.fire({
          icon: "error",
          title: "Stripe URL Not Found",
          text: res.data.message || "Could not start the Stripe checkout.",
        });
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);

        // ── Backend se minimum amount validation error aa sakti hai ──
        const backendMsg = err?.response?.data?.message;

        Swal.fire({
          icon: "error",
          title: "Error",
          text: backendMsg || "Something went wrong while starting the payment.",
        });
      });
  };

  // ── Topup requests + persisted wallet balance + minimum topup (sab ek hi call sy) ──
  // ── Sirf Authorization header — koi id body mein nahi bheji jaati ──
  const fetchTopupRequests = () => {
    if (!authToken) return;

    axios
      .post(
        `${baseUrl}/mechanicadmin/topup/viewTopupRequests`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => {
        if (res.data.success) {
          setTopupRequests(res.data.requests || []);
          setWalletBalance(res.data.walletBalance || 0);
          setMinimumTopup(res.data.minimumTopup || DEFAULT_MINIMUM_TOPUP);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    fetchTopupRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  /* ══════════════════════════════════════════════════════════════
     STATS (commission rate, topup counts) — backend
     (MechanicEarningController) se seedha computed stats aati hain.
  ══════════════════════════════════════════════════════════════ */
  const [statsRaw, setStatsRaw] = useState({
    commissionRate: 0,
    totalTopupRequests: 0,
    approvedTopups: 0,
    rejectedTopups: 0,
    pendingTopups: 0,
    completedJobsCount: 0,
    totalCommissionDeducted: 0,
  });

  // ── Sirf Authorization header — koi id body mein nahi bheji jaati ──
  const fetchMechanicEarnings = () => {
    if (!authToken) return;

    axios
      .post(
        `${baseUrl}/mechanicadmin/earnings/viewMechanicEarnings`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => {
        if (res.data.success) {
          setStatsRaw(res.data.stats);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    fetchMechanicEarnings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const {
    commissionRate,
    totalTopupRequests,
    approvedTopups,
    rejectedTopups,
    pendingTopups,
    completedJobsCount,
    totalCommissionDeducted,
  } = statsRaw;

  const statsData = [
    {
      id: 1,
      icon: <FiTrendingUp />,
      count: `Rs ${Number(commissionRate).toLocaleString("en-PK")}`,
      label: "Commission Rate",
    },
    {
      id: 2,
      icon: <FiRefreshCw />,
      count: `${totalTopupRequests}`,
      label: "Total Topup Requests",
    },
    {
      id: 3,
      icon: <FiCheckCircle />,
      count: `${approvedTopups}`,
      label: "Approved",
    },
    {
      id: 4,
      icon: <FiX />,
      count: `${rejectedTopups}`,
      label: "Rejected",
    },
    {
      id: 5,
      icon: <FiClock />,
      count: `${pendingTopups}`,
      label: "Pending",
    },
  ];

  return (
    <MechanicAdminLayout>
      <div className="me-page">
        {/* ── Page Header ── */}
        <div className="me-page-header">
          <div>
            <h2 className="me-page-title">Earnings Overview</h2>
            <p className="me-page-sub">Track your income and payment history</p>
          </div>
        </div>

        {/* ══ WALLET CARD (persisted balance + minimum topup + commission deducted) ══ */}
        <div className="me-wallet-card">
          <div className="me-wallet-left">
            <div className="me-wallet-icon">
              <FaRupeeSign />
            </div>
            <div className="me-wallet-info">
              <p className="me-wallet-label">Wallet Balance</p>
              <h3 className="me-wallet-balance">
                Rs. {Number(walletBalance).toLocaleString("en-PK")}
              </h3>
              {/* ── Minimum topup note ── */}
              <p className="me-wallet-min-note">
                Minimum Topup Amount:{" "}
                <strong>Rs. {Number(minimumTopup).toLocaleString("en-PK")}</strong>
              </p>
              {/* ── Commission deduction note ── */}
              <p className="me-wallet-deduction-note">
                Rs. {Number(commissionRate).toLocaleString("en-PK")} commission is
                deducted from your wallet on every completed job.
                {completedJobsCount > 0 && (
                  <>
                    {" "}
                    So far,{" "}
                    <strong>
                      Rs. {Number(totalCommissionDeducted).toLocaleString("en-PK")}
                    </strong>{" "}
                    deducted from {completedJobsCount} completed{" "}
                    {completedJobsCount === 1 ? "job" : "jobs"}.
                  </>
                )}
              </p>
            </div>
          </div>
          <button className="me-topup-trigger-btn" onClick={openTopup}>
            <FiPlus size={15} />
            Topup Wallet
          </button>
        </div>

        {/* ══ STAT CARDS — 5 cards, one row ══ */}
        <div className="me-stats-container">
          <div className="me-stats-grid-5">
            {statsData.map((card) => (
              <div
                key={card.id}
                className={`me-stat-card ${activeCard === card.id ? "me-stat-active" : ""}`}
                style={{ borderLeft: "4px solid #FDB714" }}
                onClick={() =>
                  setActiveCard(activeCard === card.id ? null : card.id)
                }
              >
                <div className="me-stat-icon">{card.icon}</div>
                <div className="me-stat-content">
                  <h3>{card.count}</h3>
                  <p>{card.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ MY TOPUP REQUESTS TABLE (unchanged — already dynamic) ══ */}
        <div className="me-table-section">
          <div className="me-table-header">
            <h3 className="me-table-title">My Topup Requests</h3>
            <span className="me-table-count">
              {topupRequests.length} records
            </span>
          </div>

          <div className="me-table-wrapper">
            <table className="me-table">
              <thead>
                <tr>
                  <th>REQUEST ID</th>
                  <th>AMOUNT</th>
                  <th>METHOD</th>
                  <th>PROOF</th>
                  <th>Date</th>
                  <th>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {topupRequests.map((req) => (
                  <tr key={req.id}>
                    <td>
                      <span className="me-txn-id">TOPUP-{req.id}</span>
                    </td>
                    <td className="me-amount">
                      Rs. {Number(req.amount).toLocaleString("en-PK")}
                    </td>
                    <td>{req.paymentmethod}</td>
                    <td>
                      {req.paymentproof ? (
                        <img
                          src={`${baseUrlForFile}/${req.paymentproof}`}
                          alt="Payment Proof"
                          className="catmgmt-category-table-image"
                        />
                      ) : (
                        <span>No Image</span>
                      )}
                    </td>

                    <td>
                      <span className="me-txn-date">
                        {new Date(req.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`me-status-badge ${req.status === "approved" ? "me-badge-paid" : "me-badge-pending"}`}
                      >
                        {req.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ══ TOPUP MODAL OVERLAY — Stripe only ══ */}
      {showTopup && (
        <div className="me-topup-overlay" onClick={closeTopup}>
          <div className="me-topup-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="me-topup-modal-header">
              <div className="me-topup-modal-title">
                <FaRupeeSign size={16} style={{ color: "#FDB714" }} />
                <span>Wallet Topup</span>
              </div>
              <button className="me-topup-close-btn" onClick={closeTopup}>
                <FiX size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="me-topup-modal-body">
              <div className="me-topup-cur-balance">
                <span className="me-topup-cur-label">Current Balance</span>
                <span className="me-topup-cur-val">
                  Rs. {Number(walletBalance).toLocaleString("en-PK")}
                </span>
              </div>

              <p className="me-topup-section-label">Quick Select Amount</p>
              <div
                className={`me-topup-quick-grid ${customAmt ? "me-topup-disabled-group" : ""}`}
              >
                {quickAmounts.map((amt) => (
                  <button
                    key={amt}
                    className={`me-topup-quick-btn ${selectedAmt === amt ? "me-topup-quick-active" : ""}`}
                    onClick={() => handleQuickAmt(amt)}
                    disabled={!!customAmt}
                  >
                    Rs. {amt.toLocaleString("en-PK")}
                  </button>
                ))}
              </div>

              <p className="me-topup-section-label">
                Or enter custom amount
              </p>
              <div
                className={`me-topup-custom-wrap ${selectedAmt ? "me-topup-disabled-group" : ""}`}
              >
                <span className="me-topup-rs-prefix">Rs.</span>
                <input
                  type="number"
                  className="me-topup-custom-input"
                  placeholder={`Enter amount (min Rs. ${Number(minimumTopup).toLocaleString("en-PK")})`}
                  min={minimumTopup}
                  value={customAmt}
                  onChange={(e) => handleCustomAmt(e.target.value)}
                  disabled={!!selectedAmt}
                />
              </div>

              {/* Payment method — Stripe only, same style as EquipmentBookingModal */}
              <p className="me-topup-section-label">Payment Method</p>
              <p style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#888", marginBottom: 10 }}>
                <FiLock size={13} />
                All transactions are secure and encrypted.
              </p>
              <div
                className="me-topup-pm-btn me-topup-pm-active"
                style={{ width: "100%", flexDirection: "row", justifyContent: "space-between", cursor: "default" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className="me-topup-pm-icon">💳</span>
                  <span className="me-topup-pm-label">
                    STRIPE <span style={{ color: "#888", fontWeight: 400 }}>(Debit / Credit / Wallet / Bank Account)</span>
                  </span>
                </div>
              </div>

              {finalAmt >= minimumTopup && (
                <div className="me-topup-summary" style={{ marginTop: 16 }}>
                  <span>You will pay</span>
                  <strong>Rs. {finalAmt.toLocaleString("en-PK")}</strong>
                </div>
              )}

              {/* ── Agar user ne minimum sy kam custom amount likh diya, to warning ── */}
              {customAmt !== "" && finalAmt > 0 && finalAmt < minimumTopup && (
                <p className="me-topup-min-warning">
                  Minimum topup amount is Rs. {Number(minimumTopup).toLocaleString("en-PK")}
                </p>
              )}
            </div>

            <div className="me-topup-modal-footer">
              <button className="me-topup-cancel-btn" onClick={closeTopup}>
                Cancel
              </button>
              <button
                className="me-topup-confirm-btn"
                disabled={finalAmt < minimumTopup || loading}
                onClick={handlePayWithStripe}
              >
                {loading
                  ? "Processing..."
                  : finalAmt >= minimumTopup
                    ? `Pay Rs. ${finalAmt.toLocaleString("en-PK")} with Stripe`
                    : "Select Amount"}
              </button>
            </div>
          </div>
        </div>
      )}
    </MechanicAdminLayout>
  );
}